import {
  Button,
  Dialog,
  Divider,
  ProfileAvatar,
  Typography,
  DialogPosition,
  renderIcon,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { PhotoPrompt } from '../../../../../components/photo-prompt/photo-prompt';
import { userSelectors } from '@/store/user';
import * as styles from '../../edit-practitioner-profile.styles';
import { AddPhotoProps } from './add-photo.types';
import thumbsUpImage from '@/assets/thumbsUp.png';

// Camera / gallery (native + Chrome desktop pwa-camera-modal) can fire a residual
// click when they close. That click often lands on Save/Skip and jumps past the
// "Looking good!" preview. Block with a ref (sync) — setState alone is too late
// because the residual click can run before React re-renders.
const SAVE_GUARD_MS = 1000;

export const AddPhoto: React.FC<AddPhotoProps> = ({ onSubmit, isLoading }) => {
  const acceptedMimeFormats = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
  ];

  const user = useSelector(userSelectors.getUser, shallowEqual);

  const [editProfilePictureVisible, setEditProfilePictureVisible] =
    useState(false);
  const [previewUrl, setPreviewUrl] = useState(user?.profileImageUrl ?? '');
  const [isSaveBlocked, setIsSaveBlocked] = useState(false);

  const previewUrlRef = useRef(previewUrl);
  const saveBlockedRef = useRef(false);
  const saveGuardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearSaveGuardTimeout = useCallback(() => {
    if (saveGuardTimeoutRef.current) {
      clearTimeout(saveGuardTimeoutRef.current);
      saveGuardTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSaveGuardTimeout(), [clearSaveGuardTimeout]);

  const setPreview = useCallback((url: string) => {
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  const blockSave = useCallback(
    (ms: number = SAVE_GUARD_MS) => {
      clearSaveGuardTimeout();
      // Sync immediately so residual clicks in this event loop cannot Save/Skip.
      saveBlockedRef.current = true;
      setIsSaveBlocked(true);
      saveGuardTimeoutRef.current = setTimeout(() => {
        saveBlockedRef.current = false;
        setIsSaveBlocked(false);
        saveGuardTimeoutRef.current = null;
      }, ms);
    },
    [clearSaveGuardTimeout]
  );

  const displayProfilePicturePrompt = () => {
    setEditProfilePictureVisible((visible) => !visible);
  };

  const unlockSave = useCallback(() => {
    clearSaveGuardTimeout();
    saveBlockedRef.current = false;
    setIsSaveBlocked(false);
  }, [clearSaveGuardTimeout]);

  // Camera/gallery opened — block Save/Skip until capture finishes or is cancelled.
  const handlePickerOpen = useCallback(() => {
    blockSave(60_000);
  }, [blockSave]);

  const handlePickerCancel = useCallback(() => {
    unlockSave();
  }, [unlockSave]);

  const picturePromtOnAction = (imageBaseString: string) => {
    // Keep user on this step with a preview; do not call onSubmit here.
    setPreview(imageBaseString);
    setEditProfilePictureVisible(false);
    // Allow residual picker clicks to land first, then keep Save blocked briefly.
    blockSave(SAVE_GUARD_MS);
  };

  const handleDelete = () => {
    setEditProfilePictureVisible(false);
    setPreview('');
    unlockSave();
  };

  const handlePromptClose = () => {
    setEditProfilePictureVisible(false);
    unlockSave();
  };

  const handlePrimaryAction = () => {
    // Ref check is required: state may still be stale when a residual click fires.
    if (saveBlockedRef.current || editProfilePictureVisible) {
      return;
    }
    onSubmit(previewUrlRef.current);
  };

  return (
    <>
      {previewUrl ? (
        // Row sizing lives on the image, not the wrapper — a w-24 wrapper
        // squeezed the heading onto two lines. The page already applies px-4.
        <div className="flex w-full items-center gap-3 py-6">
          <img
            src={thumbsUpImage}
            alt="Thumbs up"
            className="h-24 w-24 shrink-0"
          />
          <Typography text={'Looking good!'} type="h1" color="black" />
        </div>
      ) : (
        <Typography
          text={'Add a Photo'}
          type="h1"
          color="primary"
          className={'mt-3'}
        />
      )}

      <Typography
        text={
          previewUrl
            ? 'Edit your photo by tapping the camera icon or tap save'
            : 'Add a photo of yourself so your coach and club will recognise you.'
        }
        type="body"
        color="textDark"
        className="font-medium"
      />

      <div className={'inline-flex w-full justify-center pt-16 pb-12'}>
        <ProfileAvatar
          dataUrl={previewUrl ?? ''}
          size={'header'}
          onPressed={displayProfilePicturePrompt}
          hasConsent={true}
        />
      </div>

      <Divider dividerType="solid" className="mb-4" />

      <Button
        className="w-full"
        color="primary"
        textColor="white"
        type="filled"
        onClick={handlePrimaryAction}
        disabled={isSaveBlocked || editProfilePictureVisible}
        isLoading={isLoading}
      >
        {renderIcon(previewUrl ? 'SaveIcon' : 'ClockIcon', styles.iconPrimary)}
        <Typography
          type="h6"
          className="ml-2"
          text={previewUrl ? 'Save' : 'Skip'}
          color="white"
        />
      </Button>

      <Dialog
        visible={editProfilePictureVisible}
        position={DialogPosition.Bottom}
      >
        <div className={'p-4'}>
          <PhotoPrompt
            acceptedMimeFormats={acceptedMimeFormats}
            resolutionLimit={IMAGE_WIDTH}
            title="Profile Photo"
            onClose={handlePromptClose}
            onAction={picturePromtOnAction}
            onPickerOpen={handlePickerOpen}
            onPickerCancel={handlePickerCancel}
            onDelete={previewUrl ? handleDelete : undefined}
          ></PhotoPrompt>
        </div>
      </Dialog>
    </>
  );
};
